import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { unscopedKey } from "@/lib/queryClient";
import { Button } from "@/components/ui/Button";
import type { KratosFlow } from "@/lib/kratos";
import {
  createFlow,
  firstFlowError,
  hasMethod,
  KratosError,
  KratosFlowExpiredError,
  kratosErrorCopy,
  nodeMessages,
  submitFlow,
} from "@/lib/kratos";
import { exchangeKratosSession } from "@/features/auth/kratos/exchange";

/**
 * In-app AAL2 step-up (#358): a Kratos login flow created with
 * `aal=aal2` against the EXISTING first-factor session. Kratos renders
 * only second-factor methods (TOTP); completing it upgrades the Kratos
 * session to `aal2`, and the session exchange mirrors the new
 * `amr`/`acr` onto the app session so `session_satisfied_mfa()` passes
 * and the privileged action that 403'd can be retried.
 *
 * Two non-form outcomes matter:
 *   - Kratos answers `session_already_available` — the Kratos session
 *     ALREADY satisfies aal2 (e.g. TOTP was enrolled this session,
 *     which upgrades it in place) and only the app session is stale.
 *     Skip the form: re-exchange and continue.
 *   - No Kratos session at all — the only path is a fresh sign-in.
 *
 * Reached via `/login?aal=aal2&next=…` — the target the `apiFetch`
 * 403 `mfa_required` handler redirects to. The `login` variant is the
 * same flow rendered inline by the login/recovery panels when the
 * exchange rejects with `mfa_login_required` (the per-user MFA-at-login
 * gate) — only the copy and an optional cancel differ.
 */
export function KratosStepUpPanel({
  nextPath,
  onCompleted,
  variant = "step-up",
  onCancel,
  allowRememberDevice = false,
}: {
  nextPath: string;
  onCompleted?: () => void | Promise<void>;
  variant?: "step-up" | "login";
  onCancel?: () => void;
  /** Offer the 30-day "remember this device" checkbox (MFA-at-login only;
   * honored by the backend only when the Kratos session reached aal2). */
  allowRememberDevice?: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [flow, setFlow] = useState<KratosFlow | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [dead, setDead] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  // Checkbox state and the onCompleted prop are read through REFS inside
  // finishStepUp. Putting either in the callback deps recreates `initialize`
  // (which depends on finishStepUp), and its effect would then re-create the
  // aal2 flow. That is not just wasted work: after the second factor
  // completes, the re-created flow comes back `session_already_available`,
  // whose handler calls finishStepUp AGAIN — a duplicate exchange that
  // double-minted trusted devices (callers pass inline `onCompleted` arrows
  // whose identity changes every parent render).
  const rememberDeviceRef = useRef(false);
  rememberDeviceRef.current = rememberDevice;
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  const finishStepUp = useCallback(async () => {
    // Re-exchange: the Kratos session now carries aal2 + totp; the
    // backend re-mints the app session with the upgraded amr/acr (and,
    // when asked, a 30-day trusted-device cookie).
    await exchangeKratosSession(
      allowRememberDevice && rememberDeviceRef.current
        ? { rememberDevice: true }
        : undefined,
    );
    await queryClient.invalidateQueries();
    await queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
    const completed = onCompletedRef.current;
    if (completed) {
      await completed();
      return;
    }
    void navigate({ to: nextPath, replace: true });
  }, [allowRememberDevice, navigate, nextPath, queryClient]);

  const initialize = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setFlow(await createFlow("login", { aal: "aal2" }));
    } catch (err) {
      if (isSessionAlreadySatisfied(err)) {
        // The Kratos session already meets aal2 — only the app
        // session is stale. No second factor to collect.
        try {
          await finishStepUp();
          return;
        } catch (exchangeErr) {
          setError(
            exchangeErr instanceof Error
              ? exchangeErr.message
              : "Could not refresh your session.",
          );
        }
      } else {
        setError(kratosErrorCopy(err));
        setDead(true);
      }
    } finally {
      setLoading(false);
    }
  }, [finishStepUp]);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  // Which second factors this aal2 flow offers (node-driven; a user may
  // have TOTP, backup codes, or both enrolled). Default to TOTP when
  // present; the backup-code input is one toggle away.
  const totpAvailable = !!flow && hasMethod(flow, "totp");
  const lookupAvailable = !!flow && hasMethod(flow, "lookup_secret");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const lookupMode = lookupAvailable && (useBackupCode || !totpAvailable);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !flow) return;
    setSubmitting(true);
    setError(null);
    const values = lookupMode
      ? { method: "lookup_secret", lookup_secret: code.trim() }
      : { method: "totp", totp_code: code.trim() };
    try {
      let outcome;
      try {
        outcome = await submitFlow("login", flow, values);
      } catch (err) {
        if (!(err instanceof KratosFlowExpiredError)) throw err;
        const fresh = await createFlow("login", { aal: "aal2" });
        setFlow(fresh);
        outcome = await submitFlow("login", fresh, values);
      }
      if (outcome.kind === "success") {
        await finishStepUp();
        return;
      }
      setFlow(outcome.flow);
    } catch (err) {
      setError(kratosErrorCopy(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <p data-testid="step-up-loading" className="font-mono text-xs text-ink-muted">
        Preparing verification...
      </p>
    );
  }

  if (!flow) {
    // No aal2 flow and not already satisfied: the first-factor Kratos
    // session is gone (expired, signed out). A fresh sign-in lands
    // back here via `next` if step-up is still required.
    return (
      <div
        data-testid="step-up-no-session"
        className="flex flex-col gap-2 rounded border border-rule bg-elevated p-3"
      >
        <p className="text-sm text-ink">
          {dead && error ? error : "Your sign-in session has ended."}
        </p>
        <Link
          to="/login"
          search={{ next: nextPath }}
          className="font-mono text-xs text-accent hover:underline"
        >
          Sign in again
        </Link>
      </div>
    );
  }

  const flowError = firstFlowError(flow);
  const codeMessages = nodeMessages(flow, lookupMode ? "lookup_secret" : "totp_code");
  const intro = lookupMode
    ? "Enter one of your one-time backup codes."
    : "Enter the 6-digit code from your authenticator app.";

  return (
    <form
      data-testid="step-up-form"
      className="flex flex-col gap-3"
      onSubmit={handleSubmit}
    >
      <p className="flex items-center gap-2 text-sm text-ink">
        <ShieldCheck size={16} strokeWidth={2.2} aria-hidden="true" />
        {variant === "login"
          ? `Confirm it's you to finish signing in. ${intro}`
          : `This action needs a second factor. ${intro}`}
      </p>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          {lookupMode ? "Backup code" : "Authenticator code"}
        </span>
        <input
          type="text"
          inputMode={lookupMode ? "text" : "numeric"}
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          autoFocus
          data-testid={lookupMode ? "step-up-lookup-code" : "step-up-totp-code"}
          className="rounded border border-rule bg-canvas px-3 py-2 font-mono text-sm tracking-[0.25em] text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </label>
      {totpAvailable && lookupAvailable ? (
        <button
          type="button"
          onClick={() => {
            setUseBackupCode(!lookupMode);
            setCode("");
          }}
          data-testid={lookupMode ? "step-up-use-totp" : "step-up-use-backup-code"}
          className="self-start font-mono text-xs text-accent hover:underline"
        >
          {lookupMode ? "Use your authenticator app instead" : "Use a backup code instead"}
        </button>
      ) : null}
      {codeMessages.map((message) => (
        <p
          key={message}
          data-testid="step-up-code-message"
          className="font-mono text-xs text-destructive"
        >
          {message}
        </p>
      ))}
      {flowError ? (
        <p data-testid="step-up-error" className="font-mono text-xs text-destructive">
          {flowError}
        </p>
      ) : null}
      {error ? (
        <p data-testid="step-up-exchange-error" className="font-mono text-xs text-destructive">
          {error}
        </p>
      ) : null}
      <p className="font-mono text-[0.65rem] leading-relaxed text-ink-muted">
        No authenticator enrolled yet? Add one under Account → Security,
        then retry the action.
      </p>
      {allowRememberDevice ? (
        <button
          type="button"
          role="switch"
          aria-checked={rememberDevice}
          onClick={() => setRememberDevice((prev) => !prev)}
          data-testid="step-up-remember-device"
          className="flex items-center gap-2 self-start text-xs text-ink"
        >
          <span
            aria-hidden="true"
            data-checked={rememberDevice}
            className="relative h-5 w-9 shrink-0 rounded-full border border-rule bg-surface transition-colors data-[checked=true]:border-accent data-[checked=true]:bg-accent"
          >
            <span
              className={[
                "absolute top-0.5 h-3.5 w-3.5 rounded-full transition-all",
                rememberDevice ? "left-[1.125rem] bg-accent-ink" : "left-0.5 bg-ink",
              ].join(" ")}
            />
          </span>
          Remember this device for 30 days
        </button>
      ) : null}
      <Button
        type="submit"
        disabled={submitting || !code.trim()}
        data-testid="step-up-submit"
      >
        {submitting ? "Verifying..." : "Verify"}
      </Button>
      {onCancel ? (
        <button
          type="button"
          onClick={onCancel}
          data-testid="step-up-cancel"
          className="self-start font-mono text-xs text-accent hover:underline"
        >
          Back to sign in
        </button>
      ) : null}
    </form>
  );
}

function isSessionAlreadySatisfied(err: unknown): boolean {
  if (!(err instanceof KratosError)) return false;
  const id = (err.body as { error?: { id?: string } } | null)?.error?.id ?? "";
  return id === "session_already_available";
}
